import React, { useState, useEffect } from 'react';
import { Search, Filter, MessageCircle, Phone, Video, MoreHorizontal, Star, Clock, Check, CheckCheck, Send, Paperclip, Smile, ArrowLeft, User, Award, MapPin, Calendar, Heart, Package, Briefcase, ChevronRight, Plus, Archive, Trash2, UserPlus, Settings, Users, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '@/components/BottomNavigation';
import LoadingSpinner, { Skeleton, ListSkeleton } from '@/components/LoadingSpinner';

interface Message {
  id: string;
  content: string;
  timestamp: Date;
  senderId: string;
  isRead: boolean;
  type: 'text' | 'service_inquiry' | 'booking_confirmation';
}

interface Conversation {
  id: string;
  participantId: string;
  participantName: string;
  participantAvatar: string;
  participantRole: 'client' | 'provider';
  lastMessage: Message;
  unreadCount: number;
  isOnline: boolean;
  relatedService?: {
    id: string;
    title: string;
    price: number;
  };
  messages: Message[];
}

const Messages = () => {
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [messageSending, setMessageSending] = useState(false);
  const navigate = useNavigate();

  // Mock current user ID
  const currentUserId = 'user-angela';

  // Mock conversations data
  const mockConversations: Conversation[] = [
    {
      id: 'conv-1',
      participantId: 'user-emma',
      participantName: 'Emma Janssen',
      participantAvatar: 'https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=150',
      participantRole: 'client',
      isOnline: true,
      unreadCount: 2,
      relatedService: {
        id: 'service-1',
        title: 'Social Media Strategie Sessie',
        price: 85
      },
      lastMessage: {
        id: 'msg-1',
        content: 'Hoi Angela! Ik ben geïnteresseerd in je social media strategie sessie. Kun je me wat meer vertellen over wat je precies behandelt?',
        timestamp: new Date(Date.now() - 300000), // 5 minutes ago
        senderId: 'user-emma',
        isRead: false,
        type: 'service_inquiry'
      },
      messages: [
        {
          id: 'msg-1a',
          content: 'Hoi Emma! Leuk dat je interesse hebt in mijn social media sessie!',
          timestamp: new Date(Date.now() - 3600000), // 1 hour ago
          senderId: 'user-angela',
          isRead: true,
          type: 'text'
        },
        {
          id: 'msg-1b',
          content: 'In de sessie bespreken we je huidige social media aanpak, maken we een contentkalender voor de komende maand, en ik geef je concrete tips voor meer engagement.',
          timestamp: new Date(Date.now() - 3580000),
          senderId: 'user-angela',
          isRead: true,
          type: 'text'
        },
        {
          id: 'msg-1c',
          content: 'Dat klinkt precies wat ik nodig heb! Hoe lang duurt zo\'n sessie?',
          timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
          senderId: 'user-emma',
          isRead: true,
          type: 'text'
        },
        {
          id: 'msg-1d',
          content: 'De sessie duurt 90 minuten. We kunnen dit online via Zoom doen of als je in Amsterdam bent, kunnen we ook in persoon afspreken.',
          timestamp: new Date(Date.now() - 600000), // 10 minutes ago
          senderId: 'user-angela',
          isRead: true,
          type: 'text'
        },
        {
          id: 'msg-1',
          content: 'Perfect! Online via Zoom past me beter. Wanneer heb je deze week tijd?',
          timestamp: new Date(Date.now() - 300000), // 5 minutes ago
          senderId: 'user-emma',
          isRead: false,
          type: 'service_inquiry'
        }
      ]
    },
    {
      id: 'conv-2',
      participantId: 'user-sophie',
      participantName: 'Sophie van der Berg',
      participantAvatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150',
      participantRole: 'client',
      isOnline: false,
      unreadCount: 0,
      relatedService: {
        id: 'service-2',
        title: 'Websiteontwerp & Ontwikkeling',
        price: 1250
      },
      lastMessage: {
        id: 'msg-2',
        content: 'Dankjewel voor de geweldige website! Mijn klanten zijn er super enthousiast over.',
        timestamp: new Date(Date.now() - 86400000), // 1 day ago
        senderId: 'user-sophie',
        isRead: true,
        type: 'text'
      },
      messages: [
        {
          id: 'msg-2a',
          content: 'Hoi Sophie! Wat fijn dat je tevreden bent met je nieuwe website!',
          timestamp: new Date(Date.now() - 172800000), // 2 days ago
          senderId: 'user-angela',
          isRead: true,
          type: 'text'
        },
        {
          id: 'msg-2',
          content: 'Dankjewel voor de geweldige website! Mijn klanten zijn er super enthousiast over.',
          timestamp: new Date(Date.now() - 86400000), // 1 day ago
          senderId: 'user-sophie',
          isRead: true,
          type: 'text'
        }
      ]
    },
    {
      id: 'conv-3',
      participantId: 'user-lisa',
      participantName: 'Lisa Vermeulen',
      participantAvatar: 'https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=150',
      participantRole: 'client',
      isOnline: true,
      unreadCount: 1,
      lastMessage: {
        id: 'msg-3',
        content: 'Hoi! Ik zag je fotoshoot package. Is dit ook geschikt voor personal branding foto\'s?',
        timestamp: new Date(Date.now() - 7200000), // 2 hours ago
        senderId: 'user-lisa',
        isRead: false,
        type: 'service_inquiry'
      },
      messages: [
        {
          id: 'msg-3',
          content: 'Hoi! Ik zag je fotoshoot package. Is dit ook geschikt voor personal branding foto\'s?',
          timestamp: new Date(Date.now() - 7200000), // 2 hours ago
          senderId: 'user-lisa',
          isRead: false,
          type: 'service_inquiry'
        }
      ]
    }
  ];

  const [conversations, setConversations] = useState<Conversation[]>(mockConversations);

  const currentConversation = conversations.find(conv => conv.id === selectedConversation);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setMessageSending(true);

    // Simulate sending delay
    await new Promise(resolve => setTimeout(resolve, 800));

    const newMsg: Message = {
      id: 'msg-' + Date.now(),
      content: newMessage.trim(),
      timestamp: new Date(),
      senderId: currentUserId,
      isRead: true,
      type: 'text'
    };

    setConversations(prevConversations =>
      prevConversations.map(conv => {
        if (conv.id === selectedConversation) {
          return {
            ...conv,
            messages: [...conv.messages, newMsg],
            lastMessage: newMsg
          };
        }
        return conv;
      })
    );

    setNewMessage('');
    setMessageSending(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Nu';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}u`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
  };

  const filteredConversations = conversations.filter(conv =>
    conv.participantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.lastMessage.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUnreadCount = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);

  // Mark conversation as read when selected
  useEffect(() => {
    if (selectedConversation) {
      setConversations(prevConversations =>
        prevConversations.map(conv => {
          if (conv.id === selectedConversation && conv.unreadCount > 0) {
            return {
              ...conv,
              unreadCount: 0,
              messages: conv.messages.map(msg => ({ ...msg, isRead: true }))
            };
          }
          return conv;
        })
      );
    }
  }, [selectedConversation]);

  // Simulate initial loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-elegant">
        <div className="glass-effect border-b border-cream-300">
          <div className="px-6 py-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="skeleton h-8 w-8 rounded-full" />
                <div>
                  <div className="skeleton-title h-6 w-32" />
                  <div className="skeleton-text h-4 w-48" />
                </div>
              </div>
              <div className="skeleton h-8 w-20 rounded-full" />
            </div>

            <div className="card-premium p-4 mb-6">
              <div className="text-center">
                <div className="skeleton-title h-8 w-48 mx-auto mb-2" />
                <div className="skeleton-text h-4 w-64 mx-auto" />
              </div>
            </div>

            <div className="skeleton h-10 w-full rounded-lg" />
          </div>
        </div>

        <div className="px-6 py-4">
          <ListSkeleton items={5} />
        </div>

        <BottomNavigation />
      </div>
    );
  }
  if (selectedConversation && currentConversation) {
    // Individual Chat View with loading enhancements
    return (
      <div className="h-screen flex flex-col bg-elegant">
        {/* Chat Header */}
        <div className="glass-effect border-b border-cream-300 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedConversation(null)}
                className="lg:hidden hover:bg-cream-100 hover:scale-105 transition-all duration-200"
                style={{ color: '#837069' }}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <Avatar className="h-10 w-10 hover:scale-105 transition-transform duration-200">
                <AvatarImage src={currentConversation.participantAvatar} />
                <AvatarFallback className="bg-cream-200 text-navy-700">
                  {currentConversation.participantName.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold text-navy-800">{currentConversation.participantName}</h3>
                <div className="flex items-center gap-2 text-sm">
                  {currentConversation.isOnline && (
                    <>
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse-subtle"></div>
                      <span className="text-green-600">Online</span>
                    </>
                  )}
                  {currentConversation.relatedService && (
                    <>
                      {currentConversation.isOnline && <span className="text-navy-400">•</span>}
                      <span className="text-navy-500 hover:text-navy-700 transition-colors cursor-pointer">
                        {currentConversation.relatedService.title}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="hover:bg-cream-100 hover:scale-105 transition-all duration-200" style={{ color: '#837069' }}>
                <Phone className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" className="hover:bg-cream-100 hover:scale-105 transition-all duration-200" style={{ color: '#837069' }}>
                <Video className="h-4 w-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="hover:bg-cream-100 hover:scale-105 transition-all duration-200" style={{ color: '#837069' }}>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem>Conversatie archiveren</DropdownMenuItem>
                  <DropdownMenuItem>Rapporteren</DropdownMenuItem>
                  <DropdownMenuItem className="text-red-600">Blokkeren</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Service Context Banner */}
        {currentConversation.relatedService && (
          <div className="bg-cream-50 border-b border-cream-200 px-4 py-2">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-navy-600" />
                <span className="text-navy-700">Gesprek over:</span>
                <span className="font-medium text-navy-800">{currentConversation.relatedService.title}</span>
                <Badge variant="secondary" className="bg-cream-100 text-navy-700">
                  €{currentConversation.relatedService.price}
                </Badge>
              </div>
              <Button size="sm" className="btn-elegant-primary hover:scale-105 transition-all duration-200">
                Boek Nu
              </Button>
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-cream-50">
          {currentConversation.messages.map((message, index) => {
            const isOwnMessage = message.senderId === currentUserId;
            return (
              <div
                key={message.id}
                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} animate-fade-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`max-w-xs lg:max-w-md`}>
                  <div
                    className={`rounded-2xl px-4 py-2 transition-all duration-200 hover:scale-105 ${
                      isOwnMessage
                        ? 'bg-navy-700 text-white hover:bg-navy-800'
                        : 'bg-white border border-cream-300 text-navy-800 hover:shadow-elegant'
                    }`}
                  >
                    <p className="text-sm leading-relaxed">{message.content}</p>
                  </div>
                  <div className={`flex items-center gap-1 mt-1 text-xs text-navy-500 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                    <span>{formatTime(message.timestamp)}</span>
                    {isOwnMessage && (
                      <>
                        {message.isRead ? (
                          <CheckCheck className="h-3 w-3 text-navy-600" />
                        ) : (
                          <Check className="h-3 w-3" />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Typing indicator */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-white border border-cream-300 rounded-2xl px-4 py-2">
                <div className="loading-dots">
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Message Input */}
        <div className="glass-effect border-t border-cream-300 p-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Typ je bericht..."
                className="resize-none border-cream-300 focus:border-gold-400 focus:ring-gold-400 transition-all duration-200"
                disabled={messageSending}
              />
            </div>
            <Button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || messageSending}
              className="btn-elegant-primary disabled:bg-cream-300 transition-all duration-200 hover:scale-105"
            >
              {messageSending ? (
                <LoadingSpinner size="sm" variant="spinner" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-navy-500 mt-2">
            Enter om te verzenden • Professioneel en respectvol communiceren
          </p>
        </div>
      </div>
    );
  }

  // Conversations List View
  return (
    <div className="min-h-screen bg-elegant">
      {/* Header */}
      <div className="glass-effect border-b border-cream-300">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="p-2 hover:bg-cream-100"
                style={{ color: '#837069' }}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-serif text-navy-800">Berichten</h1>
                <p className="text-sm text-navy-600">
                  Connect met andere women on a mission
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {totalUnreadCount > 0 && (
                <Badge className="bg-gold-500 text-white">
                  {totalUnreadCount} nieuw
                </Badge>
              )}
              <Button variant="outline" size="sm" className="hover:bg-cream-100" style={{ borderColor: '#d9cbbf', color: '#837069' }}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Berichten Header */}
          <div className="card-premium p-4 mb-6">
            <div className="text-center">
              <div className="mb-2">
                <h1 className="script-text text-4xl text-navy-800">
                  Berichten
                </h1>
              </div>
              <p className="text-sm text-navy-600">
                Chat met andere vrouwen over diensten en projecten
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-navy-400 h-4 w-4" />
            <Input
              placeholder="Zoek in berichten..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-cream-300 focus:border-gold-400 focus:ring-gold-400"
            />
          </div>
        </div>
      </div>

      {/* Conversations List */}
      <div className="px-6 py-4 pb-24">
        {filteredConversations.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="h-12 w-12 text-navy-300 mx-auto mb-4" />
            <h3 className="text-lg font-serif text-navy-800 mb-2">
              {searchQuery ? 'Geen berichten gevonden' : 'Nog geen berichten'}
            </h3>
            <p className="text-navy-600 max-w-sm mx-auto">
              {searchQuery 
                ? 'Probeer een andere zoekterm'
                : 'Wanneer vrouwen je berichten sturen, verschijnen ze hier'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredConversations.map((conversation) => (
              <Card
                key={conversation.id}
                className="card-premium-hover cursor-pointer"
                onClick={() => setSelectedConversation(conversation.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="relative">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={conversation.participantAvatar} />
                        <AvatarFallback className="bg-cream-200 text-navy-700">
                          {conversation.participantName.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      {conversation.isOnline && (
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-serif font-semibold text-navy-800 truncate">
                          {conversation.participantName}
                        </h3>
                        <div className="flex items-center gap-2">
                          {conversation.unreadCount > 0 && (
                            <Badge className="bg-gold-500 text-white text-xs">
                              {conversation.unreadCount}
                            </Badge>
                          )}
                          <span className="text-xs text-navy-500">
                            {formatTime(conversation.lastMessage.timestamp)}
                          </span>
                        </div>
                      </div>

                      {conversation.relatedService && (
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary" className="bg-cream-100 text-navy-700 text-xs">
                            {conversation.relatedService.title}
                          </Badge>
                        </div>
                      )}

                      <p className={`text-sm truncate ${
                        conversation.unreadCount > 0 ? 'text-navy-800 font-medium' : 'text-navy-600'
                      }`}>
                        {conversation.lastMessage.content}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default Messages;